// Engagement tips formatter utilities for product journey

/**
 * Category mapping configuration for PX Topics display
 * Maps backend category keys to display titles and defines static order
 */
const CATEGORY_MAPPING = {
  // Define the order and display titles for categories
  startup: {
    title: 'Pre-Arrival & Unboxing',
    order: 1
  },
  common_problems: {
    title: 'Usage & Education', 
    order: 2
  },
  cross_sell: {
    title: 'Cross-Sell & Up-Sell',
    order: 3
  },
  usage_maintenance: {
    title: 'Care & Maintenance',
    order: 4
  }
};

/**
 * Get display title for a category key
 * @param {string} categoryKey - The backend category key
 * @returns {string} - The display title
 */
function getCategoryDisplayTitle(categoryKey) {
  const mapping = CATEGORY_MAPPING[categoryKey];
  if (mapping) {
    return mapping.title;
  }
  
  // Fallback for unmapped categories (maintains backward compatibility)
  return categoryKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Get the sort order for a category key
 * @param {string} categoryKey - The backend category key
 * @returns {number} - The sort order (lower numbers come first)
 */
function getCategorySortOrder(categoryKey) {
  const mapping = CATEGORY_MAPPING[categoryKey];
  return mapping ? mapping.order : 999; // Unmapped categories go to the end
}

/**
 * Format engagement tips content with categories and bullet points
 * @param {string|object} content - Either raw text content with category headers and bullet points,
 *                                 or structured data object
 * @returns {string} - HTML formatted content
 */
function formatContent(content) {
  // Handle both structured and legacy formats
  let tipsData;

  // Check if we're dealing with structured JSON data
  if (typeof content === 'object' && content !== null) {
    // We have structured data format
    tipsData = content;
  } else if (typeof content === 'string') {
    // Check for potentially dangerous content in string format
    if (/<script|javascript:|<iframe|onerror=|onclick=|alert\(|eval\(|document\.cookie|window\.location/i.test(content)) {
      throw new Error('Potentially unsafe content detected');
    }

    // Legacy string format handling
    // First check if it looks like a JSON string
    const trimmedContent = content.trim();
    if ((trimmedContent.startsWith('{') && trimmedContent.endsWith('}')) ||
        (trimmedContent.startsWith('[') && trimmedContent.endsWith(']'))) {
      try {
        console.log('Attempting to parse JSON string');
        tipsData = JSON.parse(trimmedContent);
        console.log('Successfully parsed JSON string');
      } catch (e) {
        console.log('JSON parsing failed, treating as markdown', e.message);
        // If parsing fails, handle as markdown text
        return formatMarkdownContent(content);
      }
    } else {
      // Check if it has markdown headers
      if (content.includes('##')) {
        console.log('Content has markdown headers, treating as markdown');
        return formatMarkdownContent(content);
      } else {
        console.log('Content does not appear to be JSON or markdown, treating as plain text');
        // Create a simple wrapper for plain text if it's not markdown
        return `<div class="prose">
                  <p>${content.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</p>
                </div>`;
      }
    }
  } else {
    throw new Error('Invalid content: Must be a string or object');
  }

  // At this point we have a tips data object
  // We need to handle different shapes of structured data
  console.log('Initial tips data keys:', Object.keys(tipsData).join(', '));
  
  // CRITICAL FIX: Check if this is just validation metadata without actual tips content
  const hasExpectedCategory = Object.keys(tipsData).some(key => 
    ['startup', 'common_problems', 'cross_sell', 'usage_maintenance'].includes(key));
  
  // Check if the content has tips with actual categories
  const hasTipsCategories = tipsData.content && tipsData.content.tips && 
    Object.keys(tipsData.content.tips).some(key => 
      ['startup', 'common_problems', 'cross_sell', 'usage_maintenance'].includes(key));
  
  // Special case for test object detection
  // If this is exactly {is_valid: true, errors: []} with no other properties
  const isExactValidationObject = 
    Object.keys(tipsData).length === 2 && 
    tipsData.is_valid === true && 
    Array.isArray(tipsData.errors) && 
    tipsData.errors.length === 0;
  
  // Check for validation data in a nested content object
  const isNestedValidationObject = 
    Object.keys(tipsData).length === 1 && 
    tipsData.content && 
    Object.keys(tipsData.content).length === 2 &&
    tipsData.content.is_valid !== undefined && 
    Array.isArray(tipsData.content.errors);
  
  // Only recognize as validation data if it has limited properties and validation indicators
  const isSimpleValidationObject = 
    Object.keys(tipsData).length <= 2 && 
    !hasExpectedCategory &&
    !hasTipsCategories &&
    (tipsData.is_valid !== undefined || tipsData.errors !== undefined);
  
  if (isExactValidationObject || isSimpleValidationObject || isNestedValidationObject) {
    console.error('This appears to be validation data only, not tips content');
    throw new Error('Invalid tips data: Contains only validation info, not actual tips content');
  }
  
  // Check for error cases first
  if (tipsData.error || (tipsData.content && tipsData.content.tips && tipsData.content.tips.error)) {
    console.log('Error detected in tips data');

    // Extract the error message from wherever it is
    let errorMessage = tipsData.error ||
                       (tipsData.content && tipsData.content.tips && tipsData.content.tips.error) ||
                       'Unknown error';

    // Throw a properly formatted error to be caught by the error handler
    throw new Error(`Failed to load engagement tips: ${errorMessage}`);
  }

  // Case 1: The tips might be in the engagementTips property as a string
  if (tipsData.engagementTips && typeof tipsData.engagementTips === 'string') {
    console.log('Found tips in engagementTips as string');

    // Special case: If it starts with "## Error", it's an error message formatted as markdown
    if (tipsData.engagementTips.includes('## Error')) {
      console.log('Error message detected in engagementTips');
      // Extract the error message after the "## Error" header
      const errorLines = tipsData.engagementTips.split('\n')
        .filter(line => line.trim() && !line.includes('## Error'))
        .map(line => line.trim().replace(/^[•-]\s*/, ''))
        .join(', ');

      throw new Error(`Failed to load engagement tips: ${errorLines}`);
    }

    return formatMarkdownContent(tipsData.engagementTips);
  }
  // Case 2: Full structured format from API with content.tips
  else if (tipsData.content && tipsData.content.tips) {
    console.log('Found structured format with content.tips');
    // Full structured data format - extract just the tips portion
    tipsData = tipsData.content.tips;
  }
  
  // Generate HTML from the tips data object
  return formatStructuredContent(tipsData);
}

/**
 * Format legacy markdown content with categories and bullet points
 * @param {string} text - Raw text content with category headers and bullet points
 * @returns {string} - HTML formatted content
 */
function formatMarkdownContent(text) {
  // Safety checks
  if (!text || typeof text !== 'string') {
    throw new Error('Invalid content: Text must be a non-empty string');
  }
  
  // Check for error message format before doing anything else
  if (text.includes('## Error')) {
    console.log('Detected error message format in markdown content');
    
    // Extract the error message after the "## Error" header
    const errorLines = text.split('\n')
      .filter(line => line.trim() && !line.includes('## Error'))
      .map(line => line.trim().replace(/^[•-]\s*/, ''))
      .join(', ');
    
    throw new Error(`Failed to load engagement tips: ${errorLines || 'Unknown error'}`);
  }
  
  // Verify the expected format - should have at least one "## Category" header
  if (!text.includes('##')) {
    throw new Error('Invalid format: Expected formatted content with ## category headers');
  }
  
  // Sanitize any HTML in the original text
  const sanitizeHtml = (str) => {
    return str
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };
  
  // Create HTML output
  let html = '';
  
  // Format any potential category titles using ## prefix - allow for both "## Title" and "##\nContent"
  // First check for ## with nothing after it until a newline
  if (/^##\s*\n/m.test(text)) {
    throw new Error('Invalid format: Empty section header detected in markdown content');
  }
  
  // Then continue with normal section splitting
  const sections = text.split(/^##\s+/m);
  
  // First element might be empty if text starts with ##
  if (sections[0].trim() === '') {
    sections.shift();
  }
  
  for (const section of sections) {
    if (section.trim() === '') continue;
    
    // Split the section into title and content
    const lines = section.split('\n');
    const title = lines.shift().trim();
    
    // Check for empty section headers
    if (title === '') {
      throw new Error('Invalid format: Empty section header detected in markdown content');
    }
    
    // Add the category header
    html += `<div class="category">${sanitizeHtml(title)}</div>`;
    
    // Process the remaining lines for bullet points
    const contentLines = lines.filter(line => line.trim() !== '');
    
    if (contentLines.length > 0) {
      html += '<ul>';
      
      for (const line of contentLines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-')) {
          const bulletContent = sanitizeHtml(trimmedLine.substring(1).trim());
          html += `<li>${bulletContent}</li>`;
        } else {
          html += `<li>${sanitizeHtml(trimmedLine)}</li>`;
        }
      }
      
      html += '</ul>';
    }
  }
  
  return html;
}

/**
 * Format structured JSON data into HTML
 * @param {object} tipsData - Object containing categorized tips
 * @returns {string} - HTML formatted content
 */
function formatStructuredContent(tipsData) {
  if (!tipsData || typeof tipsData !== 'object') {
    throw new Error(`Invalid content: Expected tips data object, got ${typeof tipsData}`);
  }

  // Last resort fallback for legacy markdown format
  if (typeof tipsData === 'string' && tipsData.includes('##')) {
    console.log('Detected markdown format string in formatStructuredContent, using formatMarkdownContent');
    return formatMarkdownContent(tipsData);
  }
  
  // Also check for validation-only data in formatStructuredContent
  const isExactValidationObject = 
    Object.keys(tipsData).length === 2 && 
    tipsData.is_valid === true && 
    Array.isArray(tipsData.errors) && 
    tipsData.errors.length === 0;
    
  if (isExactValidationObject) {
    console.error('This appears to be validation data only, not tips content');
    throw new Error('Invalid tips data: Contains only validation info, not actual tips content');
  }

  // Sanitize any HTML in the original text
  const sanitizeHtml = (str) => {
    return String(str)
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  // Create HTML output
  let html = '';
  
  // Expected categories (matching backend validation)
  const expectedCategories = ['startup', 'common_problems', 'cross_sell', 'usage_maintenance'];
  let categoriesFound = false;
  
  // Skip these properties that aren't tip categories
  const metadataProperties = ['error', 'version', 'type', 'prompt', 'generation',
    'raw_response', 'validation', 'is_valid', 'categories', 'items_count', '_silhouettes'];
  
  // Extract silhouettes data if present (added by partial-report-manager)
  const silhouettes = tipsData._silhouettes || null;
  
  // Sort categories by the defined order, then process them
  const sortedCategories = Object.entries(tipsData)
    .filter(([category]) => !metadataProperties.includes(category))
    .sort(([categoryA], [categoryB]) => {
      const orderA = getCategorySortOrder(categoryA);
      const orderB = getCategorySortOrder(categoryB);
      return orderA - orderB;
    });

  // Go through all categories in sorted order
  sortedCategories.forEach(([category, tips]) => {
    // Check for empty string keys
    if (category === '') {
      throw new Error('Invalid format: Empty category key detected in structured data');
    }
    
    // Special case for startup and other expected categories
    if (expectedCategories.includes(category)) {
      categoriesFound = true;
    }
    
    // Use mapped display title
    const displayTitle = getCategoryDisplayTitle(category);
    
    // Add the category header
    html += `<div class="category">${sanitizeHtml(displayTitle)}</div>`;
    
    // Create list for tips
    html += '<ul>';
    
    // Helper function to format any value properly (prevents [object Object])
    const formatValue = (value) => {
      if (typeof value === 'object' && value !== null) {
        try {
          // Try to stringify the object in a nice format
          const stringified = JSON.stringify(value, null, 2);
          return `<pre>${sanitizeHtml(stringified)}</pre>`;
        } catch (e) {
          // Fallback if JSON stringify fails
          return `Object with properties: ${Object.keys(value).join(', ')}`;
        }
      } else {
        // Handle primitive values
        return sanitizeHtml(String(value));
      }
    };
    
    // Ensure tips is an array to prevent errors
    if (!Array.isArray(tips)) {
      // Single value case
      html += `<li>${formatValue(tips)}</li>`;
    } else if (tips.length === 0) {
      // Handle empty arrays
      html += `<li>No information available</li>`;
    } else {
      // Process array of tips
      tips.forEach(tip => {
        html += `<li>${formatValue(tip)}</li>`;
      });
      
      // Add silhouette tips for this category if available
      if (silhouettes && silhouettes[category] && Array.isArray(silhouettes[category])) {
        // Add a wrapper div for the silhouettes
        html += `<div class="silhouette-wrapper">`;
        
        silhouettes[category].forEach(silhouette => {
          html += `<div class="tip-silhouette">
            <div class="silhouette-content">
              <div class="tip-silhouette-title">${sanitizeHtml(silhouette)}</div>
              <div class="tip-silhouette-content"></div>
              <div class="tip-silhouette-content"></div>
            </div>
          </div>`;
        });
        
        html += `</div>`;
      }
    }
    
    html += '</ul>';
  });
  
  // Last chance: check for legacy format string property
  // Special case: if we have engagementTips as a string with markdown format, use that
  if (html === '' && tipsData.engagementTips && typeof tipsData.engagementTips === 'string' && 
      tipsData.engagementTips.includes('##')) {
    console.log('Using engagementTips string property as markdown');
    return formatMarkdownContent(tipsData.engagementTips);
  }
  
  // Special case: if we found no valid categories but this looks like the entire API response
  if (html === '' && tipsData.content && tipsData.content.tips) {
    console.log('No categories found, trying content.tips');
    return formatStructuredContent(tipsData.content.tips);
  }
  
  // If we didn't generate any content, there's a problem
  if (html === '') {
    console.error('Failed to find valid categories in tips data');
    
    // Last resort fallback: if we have engagementTips as a string, try to use that
    if (tipsData.engagementTips && typeof tipsData.engagementTips === 'string') {
      console.log('Falling back to engagementTips string property');
      return formatMarkdownContent(tipsData.engagementTips);
    }
    
    throw new Error('No valid categories found in tips data');
  }

  return html;
}

// Export utilities
window.tipsFormatter = {
  formatContent,
  formatMarkdownContent,
  formatStructuredContent
};